/**
 * Tidying the end of a reply that was cut off (R1 UX1).
 *
 * When someone presses Stop, the panel keeps the reply exactly as far as it
 * had streamed, and a stream can stop in the middle of Markdown: a bullet
 * whose bold label had only opened ("- **") rendered as a stray "**" bullet
 * (BV2). This drops markup left open at the cut: trailing lines holding only
 * list, heading, emphasis or code markers, and an unclosed bold opener on the
 * last line. The words are never changed — only markup at the very end.
 */

/** A line holding nothing but markers: an empty bullet or number, a bare heading, emphasis or code fence. */
const MARKER_ONLY_LINE = /^\s*(?:[-*+]|\d+[.)])?\s*(?:\*{1,3}|_{1,3}|`{1,3}|#{1,6})?\s*$/

/** An emphasis opener at the very end, after a space ("Trailing opener **"). */
const TRAILING_OPENER = /\s(?:\*{1,3}|_{1,3}|`{1,3})$/

function lastLineStart(text: string): number {
  return text.lastIndexOf('\n') + 1
}

export function trimDanglingMarkdown(text: string): string {
  let result = text.trimEnd()

  // Marker-only tail lines, one at a time ("- **", "1. ", "## ", "```").
  while (result.length > 0) {
    const start = lastLineStart(result)
    if (!MARKER_ONLY_LINE.test(result.slice(start))) break
    result = result.slice(0, start).trimEnd()
  }

  result = result.replace(TRAILING_OPENER, '').trimEnd()

  // An unclosed bold opener on the last line: drop it, keep its words.
  const start = lastLineStart(result)
  const lastLine = result.slice(start)
  if ((lastLine.match(/\*\*/g) ?? []).length % 2 === 1) {
    const at = lastLine.lastIndexOf('**')
    result = result.slice(0, start) + lastLine.slice(0, at) + lastLine.slice(at + 2)
  }

  return result.trimEnd()
}
